import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./AdminStyles.css";

const API_BASE = process.env.REACT_APP_API_URL || 'https://ceindiazone2.in';

const SERVICES = [
    { id: "all", label: "All Services" },
    { id: "Hindi Service", label: "Sunday Hindi Service (6am – 8am)", color: "#f59e0b" },
    { id: "English Service", label: "Sunday English Service (10am – 1pm)", color: "#3b82f6" },
    { id: "Marathi Service", label: "Sunday Marathi Service (5pm – 7pm)", color: "#10b981" },
    { id: "Lunch Hour", label: "Wednesday Lunch Hour (1pm – 2pm)", color: "#8b5cf6" },
    { id: "Wednesday Evening", label: "Wednesday Evening Service (7pm – 9pm)", color: "#ec4899" }
];

const AdminDashboard = () => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [adminName, setAdminName] = useState("Admin");
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);

    // Filters
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedService, setSelectedService] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const navigate = useNavigate();

    const getAuthHeader = useCallback(() => {
        const adminUserStr = localStorage.getItem("adminUser");
        if (!adminUserStr || adminUserStr === "undefined" || adminUserStr === "null") {
            return null;
        }
        try {
            const adminUser = JSON.parse(adminUserStr);
            return `Bearer ${adminUser.currentSessionToken}`;
        } catch {
            return null;
        }
    }, []);

const determineServiceFromTimestamp = (submissionTime) => {
    if (!submissionTime) return "General Service";
    const d = new Date(submissionTime);
    const day = d.getDay(); // 0 is Sunday, 3 is Wednesday
    const hours = d.getHours() + d.getMinutes() / 60;

    if (day === 0) {
        if (hours >= 5.0 && hours < 9.5) return "Sunday Hindi Service (6am – 8am)";
        if (hours >= 9.5 && hours < 14.5) return "Sunday English Service (10am – 1pm)";
        if (hours >= 14.5 && hours < 21.0) return "Sunday Marathi Service (5pm – 7pm)";
        return "Sunday Service";
    } else if (day === 3) {
        if (hours >= 12.0 && hours < 15.5) return "Wednesday Lunch Hour (1pm – 2pm)";
        if (hours >= 18.0 && hours <= 23.0) return "Wednesday Evening Service (7pm – 9pm)";
        return "Wednesday Mid-Week Service";
    }
    return "Special Service";
};

    const fetchAttendanceData = useCallback(async () => {
        setIsLoading(true);
        const authHeader = getAuthHeader();
        if (!authHeader) {
            localStorage.removeItem("adminUser");
            navigate("/admin/login");
            return;
        }

        try {
            const url = `${API_BASE}/api/admin/attendance`;
            const res = await fetch(url, {
                headers: { "Authorization": authHeader }
            });

            if (res.ok) {
                const rawData = await res.json();
                const processed = rawData.map((item, idx) => {
                    const rawTime = item.submissionTime;
                    let sName = item.serviceName;
                    let dStr = item.date;
                    let fTime = item.formattedTime;

                    if (rawTime) {
                        const dt = new Date(rawTime);
                        if (!dStr) {
                            dStr = dt.toISOString().split('T')[0];
                        }
                        if (!fTime) {
                            fTime = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        }
                        if (!sName) {
                            sName = determineServiceFromTimestamp(rawTime);
                        }
                    }

                    return {
                        ...item,
                        id: item.id || idx + 1,
                        serviceName: sName || "General Service",
                        date: dStr || "Unknown Date",
                        formattedTime: fTime || ""
                    };
                });
                setAttendanceData(processed);
            } else if (res.status === 401 || res.status === 403) {
                localStorage.removeItem("adminUser");
                navigate("/admin/login");
            } else {
                console.error("Failed to fetch attendance data");
            }
        } catch (error) {
            console.error("Error fetching attendance data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeader, navigate]);

    const fetchSummary = useCallback(async () => {
        const authHeader = getAuthHeader();
        if (!authHeader) return;

        try {
            const res = await fetch(`${API_BASE}/api/admin/attendance/summary`, {
                headers: { "Authorization": authHeader }
            });
            if (res.ok) {
                const data = await res.json();
                setSummaryData(data);
            }
        } catch (error) {
            console.error("Error fetching summary:", error);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        const adminUserStr = localStorage.getItem("adminUser");
        if (!adminUserStr || adminUserStr === "undefined" || adminUserStr === "null") {
            navigate("/admin/login");
            return;
        }
        try {
            const adminUser = JSON.parse(adminUserStr);
            setAdminName(adminUser.name || "Admin");
        } catch {
            navigate("/admin/login");
        }
    }, [navigate]);

    useEffect(() => {
        fetchAttendanceData();
        fetchSummary();
    }, [fetchAttendanceData, fetchSummary]);

    const handleLogout = () => {
        localStorage.removeItem("adminUser");
        navigate("/admin/login");
    };

    // Filter attendance data by date, service, and search
    const filteredAttendanceData = useMemo(() => {
        return attendanceData.filter(item => {
            if (selectedDate && item.date !== selectedDate) return false;
            if (selectedService && selectedService !== "all") {
                if (!item.serviceName.toLowerCase().includes(selectedService.toLowerCase())) return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchName = item.userName && item.userName.toLowerCase().includes(q);
                const matchEmail = item.userEmail && item.userEmail.toLowerCase().includes(q);
                if (!matchName && !matchEmail) return false;
            }
            return true;
        });
    }, [attendanceData, selectedDate, selectedService, searchQuery]);

    // Calculate dynamic stats from current filtered data
    const currentTotalAttendees = useMemo(() => {
        return filteredAttendanceData.reduce((sum, item) => sum + (item.count || 0), 0);
    }, [filteredAttendanceData]);

    const uniqueDatesCount = useMemo(() => {
        const set = new Set(filteredAttendanceData.map(d => d.date).filter(Boolean));
        return set.size;
    }, [filteredAttendanceData]);

    // Group current attendance by Date and Service for the visual breakdown cards
    const groupedBreakdown = useMemo(() => {
        const map = {};
        filteredAttendanceData.forEach(item => {
            const d = item.date || "Unknown Date";
            const s = item.serviceName || "General Service";
            if (!map[d]) map[d] = {};
            if (!map[d][s]) map[d][s] = { totalAttendees: 0, submissions: 0 };
            map[d][s].totalAttendees += item.count || 0;
            map[d][s].submissions += 1;
        });
        return map;
    }, [filteredAttendanceData]);

    // Download formatted Excel Report matching the user's active selection
    const downloadReport = () => {
        setIsDownloading(true);
        try {
            const recordsToExport = filteredAttendanceData;

            if (recordsToExport.length === 0) {
                alert("No attendance records to export for the selected filter.");
                setIsDownloading(false);
                return;
            }

            // Sheet 1: Detailed Attendance Records
            const detailedRows = recordsToExport.map((item, index) => ({
                "S.No": index + 1,
                "Date": item.date || "",
                "Service Name": item.serviceName || "",
                "Member Name": item.userName || "Unknown",
                "Email / Identifier": item.userEmail || "",
                "Attendees Count": item.count || 1,
                "Platform": item.platform || "Web",
                "Submission Time": item.formattedTime || (item.submissionTime ? new Date(item.submissionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "")
            }));

            // Total Attendees row
            const totalCount = recordsToExport.reduce((sum, item) => sum + (item.count || 0), 0);
            detailedRows.push({
                "S.No": "",
                "Date": "",
                "Service Name": "",
                "Member Name": "TOTAL ATTENDEES",
                "Email / Identifier": "",
                "Attendees Count": totalCount,
                "Platform": "",
                "Submission Time": ""
            });

            // Sheet 2: Service Summary by Date
            const summaryMap = {};
            recordsToExport.forEach(item => {
                const d = item.date || "Unknown Date";
                const s = item.serviceName || "General Service";
                const key = `${d}___${s}`;
                if (!summaryMap[key]) {
                    summaryMap[key] = {
                        "Date": d,
                        "Service Name": s,
                        "Total Attendees": 0,
                        "Device Check-ins": 0
                    };
                }
                summaryMap[key]["Total Attendees"] += (item.count || 0);
                summaryMap[key]["Device Check-ins"] += 1;
            });
            const summaryRows = Object.values(summaryMap);

            const wb = XLSX.utils.book_new();
            const ws1 = XLSX.utils.json_to_sheet(detailedRows);
            const ws2 = XLSX.utils.json_to_sheet(summaryRows);

            ws1['!cols'] = [
                { wch: 6 },
                { wch: 14 },
                { wch: 38 },
                { wch: 26 },
                { wch: 30 },
                { wch: 16 },
                { wch: 12 },
                { wch: 18 }
            ];

            ws2['!cols'] = [
                { wch: 14 },
                { wch: 38 },
                { wch: 18 },
                { wch: 20 }
            ];

            XLSX.utils.book_append_sheet(wb, ws1, "Detailed Attendance");
            XLSX.utils.book_append_sheet(wb, ws2, "Service Summary");

            let filenameParts = ["Attendance"];
            if (selectedService && selectedService !== "all") {
                filenameParts.push(selectedService.replace(/[^a-zA-Z0-9]/g, '_'));
            } else {
                filenameParts.push("All_Services");
            }
            if (selectedDate) {
                filenameParts.push(selectedDate);
            }
            const filename = `${filenameParts.join('_')}.xlsx`;

            XLSX.writeFile(wb, filename);
        } catch (error) {
            console.error("Excel export error:", error);
            alert("Error exporting Excel file");
        } finally {
            setIsDownloading(false);
        }
    };

    const getServiceBadgeClass = (serviceName = "") => {
        const s = serviceName.toLowerCase();
        if (s.includes("hindi")) return "badge-hindi";
        if (s.includes("english") && !s.includes("wednesday")) return "badge-english";
        if (s.includes("marathi")) return "badge-marathi";
        if (s.includes("lunch")) return "badge-lunch";
        if (s.includes("wednesday")) return "badge-wednesday";
        return "badge-default";
    };

    return (
        <div className="admin-dashboard-root">
            {/* TOP NAVIGATION BAR */}
            <header className="ad-header">
                <div className="ad-brand">
                    <span className="ad-church-pill">CHRIST EMBASSY PUNE</span>
                    <h1 className="ad-title">Admin Attendance Portal</h1>
                </div>

                <div className="ad-header-actions">
                    <div className="ad-admin-badge">
                        👤 {adminName}
                    </div>
                    <button onClick={handleLogout} className="ad-btn-logout">
                        Logout
                    </button>
                </div>
            </header>

            <main className="ad-main-content">
                {/* KPI METRIC CARDS */}
                <div className="ad-stats-grid">
                    <div className="ad-stat-card">
                        <div className="ad-stat-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                            👥
                        </div>
                        <div className="ad-stat-info">
                            <span className="ad-stat-label">Filtered Attendees</span>
                            <strong className="ad-stat-value">{currentTotalAttendees}</strong>
                            <span className="ad-stat-sub">Across {filteredAttendanceData.length} submissions</span>
                        </div>
                    </div>

                    <div className="ad-stat-card">
                        <div className="ad-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                            📊
                        </div>
                        <div className="ad-stat-info">
                            <span className="ad-stat-label">All-Time Attendees</span>
                            <strong className="ad-stat-value">{summaryData?.totalAttendees ?? currentTotalAttendees}</strong>
                            <span className="ad-stat-sub">{summaryData?.totalSubmissions ?? attendanceData.length} total device records</span>
                        </div>
                    </div>

                    <div className="ad-stat-card">
                        <div className="ad-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                            📅
                        </div>
                        <div className="ad-stat-info">
                            <span className="ad-stat-label">Dates Tracked</span>
                            <strong className="ad-stat-value">{uniqueDatesCount}</strong>
                            <span className="ad-stat-sub">{selectedDate ? `Selected: ${selectedDate}` : "Viewing all dates"}</span>
                        </div>
                    </div>

                    <div className="ad-stat-card">
                        <div className="ad-stat-icon" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                            ⛪
                        </div>
                        <div className="ad-stat-info">
                            <span className="ad-stat-label">Active Services</span>
                            <strong className="ad-stat-value">5 Main</strong>
                            <span className="ad-stat-sub">Sun (Hindi, Eng, Mar) & Wed (Lunch, Eve)</span>
                        </div>
                    </div>
                </div>

                {/* FILTER TOOLBAR */}
                <div className="ad-filter-card">
                    <div className="ad-filter-header">
                        <h2>🔍 Service & Date Filter</h2>
                        <button 
                            onClick={downloadReport} 
                            className="ad-btn-download"
                            disabled={isDownloading}
                        >
                            {isDownloading ? "Generating Excel..." : "📥 Download Excel Report"}
                        </button>
                    </div>

                    <div className="ad-filters-row">
                        {/* Service Filter */}
                        <div className="ad-filter-group">
                            <label>Filter By Service:</label>
                            <select
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)}
                                className="ad-select"
                            >
                                {SERVICES.map(s => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Filter */}
                        <div className="ad-filter-group">
                            <label>Filter By Date:</label>
                            <div className="ad-date-input-wrap">
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="ad-date-input"
                                />
                                {selectedDate && (
                                    <button 
                                        type="button" 
                                        onClick={() => setSelectedDate("")}
                                        className="ad-date-clear-btn"
                                        title="Clear Date"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Search Query */}
                        <div className="ad-filter-group ad-filter-search">
                            <label>Search Member:</label>
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="ad-search-input"
                            />
                        </div>

                        {/* Reset All */}
                        {(selectedDate || selectedService !== "all" || searchQuery) && (
                            <div className="ad-filter-group ad-filter-reset">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedDate("");
                                        setSelectedService("all");
                                        setSearchQuery("");
                                    }}
                                    className="ad-btn-reset"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* SERVICE SUMMARY BREAKDOWN BY DATE */}
                {Object.keys(groupedBreakdown).length > 0 && (
                    <div className="ad-breakdown-section">
                        <h3 className="ad-section-title">📊 Attendance Breakdown by Date & Service</h3>
                        <div className="ad-breakdown-grid">
                            {Object.entries(groupedBreakdown).map(([dateStr, services]) => {
                                const totalForDate = Object.values(services).reduce((sum, s) => sum + s.totalAttendees, 0);
                                return (
                                    <div key={dateStr} className="ad-date-summary-card">
                                        <div className="ad-date-card-header">
                                            <div>
                                                <span className="ad-card-date">{dateStr}</span>
                                            </div>
                                            <div className="ad-date-total-pill">
                                                Total: <strong>{totalForDate}</strong>
                                            </div>
                                        </div>

                                        <div className="ad-date-service-list">
                                            {Object.entries(services).map(([serviceName, stat]) => (
                                                <div key={serviceName} className="ad-date-service-row">
                                                    <span className={`ad-service-badge ${getServiceBadgeClass(serviceName)}`}>
                                                        {serviceName}
                                                    </span>
                                                    <div className="ad-service-counts">
                                                        <span className="ad-count-val">{stat.totalAttendees} <small>attendees</small></span>
                                                        <span className="ad-sub-count">({stat.submissions} devices)</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* DETAILED ATTENDANCE TABLE */}
                <div className="ad-table-card">
                    <div className="ad-table-header">
                        <h3>Detailed Attendance Records</h3>
                        <span className="ad-records-count">Showing {filteredAttendanceData.length} records</span>
                    </div>

                    <div className="ad-table-responsive">
                        {isLoading ? (
                            <div className="ad-loading-state">
                                <div className="ad-spinner"></div>
                                <p>Loading attendance data from database...</p>
                            </div>
                        ) : filteredAttendanceData.length > 0 ? (
                            <table className="ad-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th>Service Name</th>
                                        <th>Date & Time</th>
                                        <th>Member Name</th>
                                        <th>Email / Identifier</th>
                                        <th style={{ textAlign: 'center' }}>Attendees Count</th>
                                        <th>Platform</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAttendanceData.map((record, index) => (
                                        <tr key={record.id || index}>
                                            <td className="ad-td-num">{index + 1}</td>
                                            <td>
                                                <span className={`ad-service-badge ${getServiceBadgeClass(record.serviceName)}`}>
                                                    {record.serviceName || "General Service"}
                                                </span>
                                            </td>
                                            <td className="ad-td-datetime">
                                                <strong>{record.date}</strong>
                                                <span>{record.formattedTime || ""}</span>
                                            </td>
                                            <td className="ad-td-name">
                                                <strong>{record.userName}</strong>
                                            </td>
                                            <td className="ad-td-email">
                                                {record.userEmail}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="ad-count-badge">
                                                    {record.count}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="ad-platform-pill">
                                                    {record.platform || "Web"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="ad-empty-state">
                                <div className="ad-empty-icon">📭</div>
                                <h4>No attendance records found</h4>
                                <p>No check-in records match the selected date or service filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
